import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import { fetchMyPublishedInfrastructureEnquiry } from '../../services/infrastructureLeasingListing.service';
import { formatInfrastructureLeasingUrgency } from '../../constants/infrastructureLeasingUrgencyOptions';

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function urgencyLabel(u) {
  return formatInfrastructureLeasingUrgency(u);
}

function formatSubmittedAt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return str(iso);
    return d.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return str(iso);
  }
}

/**
 * Read-only summary of the buyer’s submitted enquiry (loads from API when opened).
 */
export default function InfrastructureLeasingViewEnquiryModal({ open, onClose, listingId }) {
  const suffix = listingId != null ? String(listingId) : 'x';
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || listingId == null || listingId === '') {
      return undefined;
    }
    const idNum = Number(listingId);
    if (!Number.isFinite(idNum) || idNum < 1) {
      return undefined;
    }
    let cancel = false;
    setLoading(true);
    setLoadError('');
    setDetail(null);
    (async () => {
      try {
        const res = await fetchMyPublishedInfrastructureEnquiry(idNum);
        if (cancel) return;
        if (res?.success && res?.data) {
          setDetail(res.data);
        } else {
          setLoadError(str(res?.message) || 'Could not load enquiry.');
        }
      } catch (e) {
        if (!cancel) {
          const msg = e?.response?.data?.message;
          setLoadError(typeof msg === 'string' ? msg : 'Could not load enquiry.');
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, listingId]);

  if (!open) {
    return null;
  }

  const a = detail?.asset;
  const en = detail?.enquiry;
  const c = detail?.contact;

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={onClose} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-view-enq-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button type="button" className="ile-modal-panel__close" onClick={onClose} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-view-enq-title-${suffix}`}>
              Your enquiry
            </h2>
            <p className="ile-modal-panel__subtitle">
              Submitted details for this listing
              {detail?.submittedAt ? (
                <span className="d-block text-muted small mt-1">{formatSubmittedAt(detail.submittedAt)}</span>
              ) : null}
            </p>
          </div>

          <div className="ile-modal-panel__form">
            <div className="ile-modal-panel__body">
              {loading && (
                <p className="text-secondary small mb-0 py-3 text-center">Loading…</p>
              )}
              {!loading && loadError && (
                <p className="text-danger small mb-0 py-2" role="alert">
                  {loadError}
                </p>
              )}
              {!loading && detail && !loadError && (
                <>
                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Asset</h3>
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4 text-secondary">Asset name</dt>
                      <dd className="col-sm-8 mb-1">{str(a?.name) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Asset type</dt>
                      <dd className="col-sm-8 mb-1">{str(a?.type) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary mb-0">Location</dt>
                      <dd className="col-sm-8 mb-0">{str(a?.location) || '—'}</dd>
                    </dl>
                  </div>

                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Enquiry</h3>
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4 text-secondary">Start date</dt>
                      <dd className="col-sm-8 mb-1">{str(en?.startDate) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Duration</dt>
                      <dd className="col-sm-8 mb-1">
                        {en?.durationMonths != null ? `${en.durationMonths} months` : '—'}
                      </dd>
                      <dt className="col-sm-4 text-secondary">Budget</dt>
                      <dd className="col-sm-8 mb-1">{str(en?.budget) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Area required</dt>
                      <dd className="col-sm-8 mb-1">{str(en?.areaRequired) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Urgency</dt>
                      <dd className="col-sm-8 mb-1">{urgencyLabel(en?.urgency)}</dd>
                      <dt className="col-sm-4 text-secondary mb-0">Attachment</dt>
                      <dd className="col-sm-8 mb-0">
                        {en?.attachment?.name
                          ? `${str(en.attachment.name)}${en.attachment.size != null ? ` (${en.attachment.size} bytes)` : ''}`
                          : '—'}
                      </dd>
                    </dl>
                    <div className="mt-2">
                      <span className="ile-form-label">Requirement message</span>
                      <div className="input-pill ile-readonly-message">{str(en?.message) || '—'}</div>
                    </div>
                  </div>

                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Your contact</h3>
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4 text-secondary">Name</dt>
                      <dd className="col-sm-8 mb-1">{str(c?.name) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Email</dt>
                      <dd className="col-sm-8 mb-1">{str(c?.email) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary">Phone</dt>
                      <dd className="col-sm-8 mb-1">{str(c?.phone) || '—'}</dd>
                      <dt className="col-sm-4 text-secondary mb-0">Company</dt>
                      <dd className="col-sm-8 mb-0">{str(c?.company) || '—'}</dd>
                    </dl>
                  </div>
                </>
              )}
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="ile-modal-btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
